/**
 * Bridge Session Service
 * API service for Bridge Session operations (flow, scope, Bridge Card).
 * @feature BRIDGE_CARD - getBridgeCardFromObjective, getBridgeCardData
 * @feature BRIDGE_SESSION_FLOW - Session state, entropy, scope, objectives
 */

import { apiClient } from './apiClient';
import { API_ENDPOINTS } from './api/config';
import {
  fetchSessionStateFromLocalApi,
  fetchEntropyMapFromLocalApi,
  fetchBridgeCardFromLocalApi,
  fetchProductDomainSessionState,
  fetchProductDomainEntropyMap,
  fetchProductDomainBridgeCardFromLocalApi,
  isLocalBridgeApiEnabled,
} from './bridgeSessionDataAdapter';
import {
  BridgeSessionState,
  EntropyScoresResponse,
  EntropyMapResponse,
  CreateObjectiveRequest,
  CreateObjectiveResponse,
  PlatformInitStatus,
  DomainHierarchyResponse,
  ScopeConfiguration,
  BridgeSessionScope,
  ScopeValidation,
} from '../types/bridgeSession';
import { BridgeCardObjective } from '../types/bridgeCard';

/** POST /sessions/{id}/complete (ISSUE-219) */
export interface SessionCompleteResponse {
  intentml_url: string;
  import_ready: boolean;
  session_id: string;
}

export const bridgeSessionService = {
  /**
   * Check platform initialization status
   */
  async checkPlatformInitialization(companyId: string): Promise<PlatformInitStatus> {
    try {
      const response = await apiClient.get<PlatformInitStatus>(
        API_ENDPOINTS.COMPANIES.PLATFORM_INITIALIZATION(companyId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to check platform initialization:', error);
      throw error;
    }
  },

  /**
   * Get Bridge Session state. Uses Local Bridge API when configured and available.
   * @param sessionType - 'business' for bridge-sessions, 'product' for product-domain-sessions
   */
  async getSessionState(sessionId: string, sessionType: 'business' | 'product' = 'business'): Promise<BridgeSessionState> {
    if (isLocalBridgeApiEnabled()) {
      const local = sessionType === 'product'
        ? await fetchProductDomainSessionState(sessionId)
        : await fetchSessionStateFromLocalApi(sessionId);
      if (local) return local;
    }
    try {
      const response = await apiClient.get<BridgeSessionState>(
        API_ENDPOINTS.BRIDGE_SESSIONS.STATE(sessionId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get session state:', error);
      throw error;
    }
  },

  /**
   * Get domain/subdomain hierarchy
   */
  async getDomainHierarchy(sessionId: string): Promise<DomainHierarchyResponse> {
    try {
      const response = await apiClient.get<DomainHierarchyResponse>(
        API_ENDPOINTS.BRIDGE_SESSIONS.DOMAIN_HIERARCHY(sessionId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get domain hierarchy:', error);
      throw error;
    }
  },

  /**
   * Mark domain as complete
   */
  async markDomainComplete(
    sessionId: string,
    domainId: string
  ): Promise<BridgeSessionState> {
    try {
      const response = await apiClient.post<BridgeSessionState>(
        API_ENDPOINTS.BRIDGE_SESSIONS.DOMAIN_COMPLETE(sessionId, domainId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to mark domain complete:', error);
      throw error;
    }
  },

  /**
   * Get entropy scores for all domains (legacy endpoint). Uses Local Bridge API when configured.
   * @param sessionType - 'business' for bridge-sessions, 'product' for product-domain-sessions
   */
  async getEntropyScores(sessionId: string, sessionType: 'business' | 'product' = 'business'): Promise<EntropyScoresResponse> {
    if (isLocalBridgeApiEnabled()) {
      const local = sessionType === 'product'
        ? await fetchProductDomainEntropyMap(sessionId)
        : await fetchEntropyMapFromLocalApi(sessionId);
      if (local)
        return { scores: local.scores, calculatedAt: local.calculated_at };
    }
    try {
      const response = await apiClient.get<EntropyScoresResponse>(
        API_ENDPOINTS.BRIDGE_SESSIONS.ENTROPY_SCORES(sessionId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get entropy scores:', error);
      throw error;
    }
  },

  /**
   * Get entropy map with artifact_item details. Uses Local Bridge API when configured.
   * @param sessionType - 'business' for bridge-sessions, 'product' for product-domain-sessions
   */
  async getEntropyMap(sessionId: string, scope?: string[], sessionType: 'business' | 'product' = 'business'): Promise<EntropyMapResponse> {
    if (isLocalBridgeApiEnabled()) {
      const local = sessionType === 'product'
        ? await fetchProductDomainEntropyMap(sessionId)
        : await fetchEntropyMapFromLocalApi(sessionId);
      if (local) return local;
    }
    try {
      const params = scope ? { scope: scope.join(',') } : {};
      const response = await apiClient.get<EntropyMapResponse>(
        API_ENDPOINTS.BRIDGE_SESSIONS.ENTROPY_MAP(sessionId),
        { params }
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get entropy map:', error);
      throw error;
    }
  },

  /**
   * Create objective from selected subdomain
   */
  async createObjective(
    sessionId: string,
    request: CreateObjectiveRequest
  ): Promise<CreateObjectiveResponse> {
    try {
      const response = await apiClient.post<CreateObjectiveResponse>(
        API_ENDPOINTS.BRIDGE_SESSIONS.OBJECTIVES(sessionId),
        request
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to create objective:', error);
      throw error;
    }
  },

  /**
   * Get Bridge Card data from Objective artifact_item. When Local Bridge API is used, objectiveArtifactItemId is the sessionId.
   * @param sessionType - 'business' for bridge-sessions, 'product' for product-domain-sessions
   */
  async getBridgeCardFromObjective(objectiveArtifactItemId: string, sessionType: 'business' | 'product' = 'business'): Promise<BridgeCardObjective> {
    if (isLocalBridgeApiEnabled()) {
      const local = sessionType === 'product'
        ? await fetchProductDomainBridgeCardFromLocalApi(objectiveArtifactItemId)
        : await fetchBridgeCardFromLocalApi(objectiveArtifactItemId);
      if (local) return local;
    }
    try {
      const response = await apiClient.get<BridgeCardObjective>(
        API_ENDPOINTS.OBJECTIVES.BRIDGE_CARD(objectiveArtifactItemId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get bridge card:', error);
      throw error;
    }
  },

  /**
   * Get Bridge Card data for objective (legacy endpoint - maps to new endpoint)
   */
  async getBridgeCardData(objectiveId: string): Promise<BridgeCardObjective> {
    // Assume objectiveId is actually objective_artifact_item_id
    return this.getBridgeCardFromObjective(objectiveId);
  },

  /**
   * Create Bridge Session with scope
   */
  async createBridgeSession(
    companyId: string,
    request: {
      objective_name: string;
      improvement_objective: string;
      target_subdomain_id?: string;
      target_correction_factor?: number;
      scope: ScopeConfiguration;
    }
  ): Promise<{
    bridge_session_id: string;
    objective_artifact_id: string;
    objective_operational_item_id: string;
    objective_improvement_item_id: string;
    scope: {
      validated: boolean;
      included_artifact_count: number;
      included_subdomain_count: number;
      excluded_artifact_count: number;
    };
  }> {
    try {
      const response = await apiClient.post<{
        bridge_session_id: string;
        objective_artifact_id: string;
        objective_operational_item_id: string;
        objective_improvement_item_id: string;
        scope: {
          validated: boolean;
          included_artifact_count: number;
          included_subdomain_count: number;
          excluded_artifact_count: number;
        };
      }>(
        API_ENDPOINTS.BRIDGE_SESSION.CREATE(companyId),
        request
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to create bridge session:', error);
      throw error;
    }
  },

  /**
   * Get scope definition for an objective
   */
  async getScope(
    companyId: string,
    objectiveId: string
  ): Promise<{
    objective_id: string;
    improvement_item_id: string;
    scope: BridgeSessionScope;
    scope_validation: ScopeValidation;
  }> {
    try {
      const response = await apiClient.get<{
        objective_id: string;
        improvement_item_id: string;
        scope: BridgeSessionScope;
        scope_validation: ScopeValidation;
      }>(
        API_ENDPOINTS.BRIDGE_SESSION.SCOPE(companyId, objectiveId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to get scope:', error);
      throw error;
    }
  },

  /**
   * Update scope for an objective
   */
  async updateScope(
    companyId: string,
    objectiveId: string,
    scope: ScopeConfiguration
  ): Promise<{
    scope: BridgeSessionScope;
    scope_validation: ScopeValidation;
  }> {
    try {
      const response = await apiClient.put<{
        scope: BridgeSessionScope;
        scope_validation: ScopeValidation;
      }>(
        API_ENDPOINTS.BRIDGE_SESSION.SCOPE(companyId, objectiveId),
        scope
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to update scope:', error);
      throw error;
    }
  },

  /**
   * Validate scope configuration
   */
  async validateScope(
    companyId: string,
    scope: ScopeConfiguration
  ): Promise<ScopeValidation> {
    try {
      const response = await apiClient.post<ScopeValidation>(
        API_ENDPOINTS.BRIDGE_SESSION.VALIDATE_SCOPE(companyId),
        scope
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to validate scope:', error);
      throw error;
    }
  },

  /**
   * ISSUE-219 — close bridge session, persist IntentML snapshot, return download URL.
   */
  async completeSession(sessionId: string): Promise<SessionCompleteResponse> {
    try {
      const response = await apiClient.post<SessionCompleteResponse>(
        API_ENDPOINTS.SESSIONS.COMPLETE(sessionId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to complete session:', error);
      throw error;
    }
  },

  /**
   * ISSUE-219 — JSON body of IntentML snapshot after session is closed.
   */
  async getIntentmlSnapshot(sessionId: string): Promise<Record<string, unknown>> {
    try {
      const response = await apiClient.get<Record<string, unknown>>(
        API_ENDPOINTS.SESSIONS.INTENTML_DOWNLOAD(sessionId)
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[BridgeSession] Failed to download IntentML snapshot:', error);
      throw error;
    }
  },
};
